import React, { useState, useEffect } from "react";
import { Popover, PopoverBody } from "reactstrap";
import { useDispatch } from "react-redux";
import moment from "moment";
import { getCustomerOrders as onGetCustomerOrders } from "../../slices/thunks";
import SalesChannels from "./SalesChannels";
import "../../assets/scss/components/_pendingOrdersPopup.scss";

const PendingOrdersPopup = ({ orderIds, targetId }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (popoverOpen && orderIds) {
      fetchOrders();
    }
  }, [popoverOpen, orderIds]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const idsArray = orderIds.split(',').map(id => id.trim()).filter(id => id);
      
      // Use existing endpoint with order IDs as filter
      const data = {
        page: 0,
        total_rows: idsArray.length,
        orderIds: idsArray.join(','),
        sort_by: "ord_id",
      };
      
      const result = await dispatch(onGetCustomerOrders(data)).unwrap();
      if (result && result.data) {
        setOrders(result.data);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (order) => {
    if (order.ord_cancellationReason) {
      return <span className="badge bg-danger text-white">Cancelled</span>;
    }
    switch (order.ord_status) {
      case 0:
        return <span className="badge bg-success text-white">Ordered</span>;
      case 1:
        return <span className="badge bg-info text-white">Pending</span>;
      case 4:
        return <span className="badge bg-danger text-white">Cancelled</span>;
      case 5:
        return <span className="badge bg-warning text-dark">Expired</span>;
      default:
        return <span className="badge bg-secondary text-white">Unknown</span>;
    }
  };

  return (
    <>
      <i
        id={targetId}
        className="ri-information-line pending-orders-icon"
        onMouseEnter={() => setPopoverOpen(true)}
        onMouseLeave={() => setPopoverOpen(false)}
      />
      <Popover
        placement="right"
        isOpen={popoverOpen}
        target={targetId}
        toggle={() => setPopoverOpen(!popoverOpen)}
        trigger="hover"
        style={{
            maxWidth: '500px',
        }}
        className="pending-orders-popover"
      >
        <PopoverBody className="p-0" style={{ maxWidth: '500px' }}>
          <div className="pending-orders-popup">
            <h6 className="mb-3 fw-bold px-3 pt-3">Pending Orders</h6>
            {loading ? (
              <div className="text-center py-3 px-3">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : orders.length > 0 ? (
              <div className="orders-list px-3 pb-3">
                {orders.map((order, index) => (
                  <div key={order.ord_id} className="order-item">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <strong className="text-primary">Order #{order.ord_id}</strong>
                        <div className="mt-1">{getStatusBadge(order)}</div>
                      </div>
                      <div className="text-end">
                        <div className="fw-semibold text-success">
                          {new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "USD",
                          }).format(order.ord_totalPrice)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="order-details">
                      <div className="detail-row">
                        <span className="label">Date:</span>
                        <span className="value">
                          {moment(order.ord_dateCreated).format("MM/DD/YYYY")}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Quantity:</span>
                        <span className="value">{order.ord_quantity}</span>
                      </div>
                      <div className="detail-row">
                        <span className="label">Customer:</span>
                        <span className="value">{order.userName || 'N/A'}</span>
                      </div>
                      {order.ord_salesChannel !== undefined && (
                        <div className="detail-row">
                          <span className="label">Channel:</span>
                          <span className="value">
                            <SalesChannels channel={order.ord_salesChannel} />
                          </span>
                        </div>
                      )}
                    </div>
                    
                    {index < orders.length - 1 && <hr className="my-2" />}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mb-0 text-muted px-3 pb-3">No orders found</p>
            )}
          </div>
        </PopoverBody>
      </Popover>
    </>
  );
};

export default PendingOrdersPopup;
